import { FC, useRef, useState, useEffect } from 'react';
import { IoCloudUploadOutline, IoTrash, IoCheckmarkCircle, IoCloseCircle, IoReload } from 'react-icons/io5';
import { useFileManager, ManagedFile, FileManagerMessages } from './useFileManager';

// Confirmação de remoção (só quando confirmRemoval está ligado). Usa window.confirm para não
// acoplar o FileManager (genérico, usado por várias telas) a um componente de diálogo de um
// domínio específico. Extraída para fora do componente (diretriz: sem função dentro de função).
const confirmIfNeeded = (enabled: boolean, message: string): boolean =>
    !enabled || window.confirm(message);

type FileManagerProps = {
    spaceName: string;
    label?: string;
    metadata?: Record<string, unknown>;
    messages?: Partial<FileManagerMessages>;
    maxHeight?: string;
    disabled?: boolean;
    // Quando true, cada remoção (por arquivo, selecionados, todos) pede confirmação antes de
    // apagar. Opcional e default false para não mudar o comportamento das telas que já usam o
    // FileManager (contract, boleto, xml); o recovery liga para o anexo de caso jurídico.
    confirmRemoval?: boolean;
    // Quando true, o arquivo sai da lista assim que o upload conclui (ver useFileManager). Para
    // telas que consolidam o fileId no onUploadComplete (recovery: cria o vínculo do anexo).
    clearOnComplete?: boolean;
    onFileIdsChange?: (fileIds: string[]) => void;
    onUploadComplete?: (fileId: string, file: File) => void;
    onUploadError?: (file: File, error: string) => void;
};

const FileManager: FC<FileManagerProps> = ({
    spaceName,
    label = 'Arquivos',
    metadata,
    messages,
    maxHeight,
    disabled = false,
    confirmRemoval = false,
    clearOnComplete = false,
    onFileIdsChange,
    onUploadComplete,
    onUploadError,
}) => {
    const {
        config,
        files,
        fileIds,
        isLoading,
        error,
        addFiles,
        removeFile,
        removeAll,
        removeSelected,
        retry,
    } = useFileManager({ spaceName, metadata, messages, clearOnComplete, onUploadComplete, onUploadError });

    const [isDragActive, setIsDragActive] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const inputRef = useRef<HTMLInputElement>(null);

    // Notify parent of fileIds changes
    useEffect(() => {
        onFileIdsChange?.(fileIds);
    }, [fileIds, onFileIdsChange]);

    // Clear selection when files change
    useEffect(() => {
        setSelectedIds(prev => {
            const currentIds = new Set(files.map(f => f.id));
            const filtered = new Set(Array.from(prev).filter(id => currentIds.has(id)));
            return filtered.size !== prev.size ? filtered : prev;
        });
    }, [files]);

    // Prevent default drag behavior on window
    useEffect(() => {
        const preventDefaults = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
        };
        window.addEventListener('dragover', preventDefaults);
        window.addEventListener('drop', preventDefaults);
        return () => {
            window.removeEventListener('dragover', preventDefaults);
            window.removeEventListener('drop', preventDefaults);
        };
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 dark:bg-slate-800">
                <p className="text-sm text-gray-500 dark:text-gray-400">Carregando configuração...</p>
            </div>
        );
    }

    if (error && !config) {
        return (
            <div className="flex items-center justify-center h-32 border-2 border-dashed border-red-300 rounded-lg bg-red-50 dark:bg-red-900/20">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
        );
    }

    const accept = config?.allowedTypes.includes('*') ? '*' : config?.allowedTypes.join(',') || '*';
    const multiple = config?.multipleFiles ?? false;
    const maxFiles = config?.multipleFiles ? config.maxFiles : 1;
    const hasAvailableSlots = files.length < maxFiles;

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        if (!disabled && e.dataTransfer?.files?.length) {
            addFiles(Array.from(e.dataTransfer.files));
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) setIsDragActive(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            addFiles(Array.from(e.target.files));
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === files.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(files.map(f => f.id)));
        }
    };

    const handleRemoveSelected = async () => {
        const ids = Array.from(selectedIds);
        if (!confirmIfNeeded(confirmRemoval, `Remover ${ids.length} arquivo(s) selecionado(s)?`)) return;
        await removeSelected(ids);
        setSelectedIds(new Set());
    };

    const handleRemoveFile = (id: string, name: string) => {
        if (!confirmIfNeeded(confirmRemoval, `Remover "${name}"?`)) return;
        removeFile(id);
    };

    const handleRemoveAll = () => {
        if (!confirmIfNeeded(confirmRemoval, 'Remover todos os arquivos?')) return;
        removeAll();
    };

    const formatSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const renderStatus = (managed: ManagedFile) => {
        switch (managed.status) {
            case 'uploading':
                return (
                    <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                            <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${managed.progress}%` }}
                            />
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{managed.progress}%</span>
                    </div>
                );
            case 'success':
                return (
                    <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                        <IoCheckmarkCircle className="w-4 h-4" />
                        <span className="text-xs">Enviado</span>
                    </span>
                );
            case 'error':
                return (
                    <span className="text-red-600 dark:text-red-400 flex items-center gap-1" title={managed.errorMessage}>
                        <IoCloseCircle className="w-4 h-4" />
                        <span className="text-xs truncate max-w-[150px]">{managed.errorMessage || 'Erro'}</span>
                    </span>
                );
            default:
                return <span className="text-gray-400 text-xs">Aguardando</span>;
        }
    };

    const allSelected = files.length > 0 && selectedIds.size === files.length;

    return (
        <div className="space-y-2">
            {/* Drop zone */}
            {!disabled && hasAvailableSlots && (
                <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => inputRef.current?.click()}
                    className={`${isDragActive ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300'}
                        flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg
                        cursor-pointer bg-gray-50 hover:bg-gray-100 dark:bg-slate-800 dark:hover:bg-slate-700 transition-colors`}
                >
                    <IoCloudUploadOutline className="text-4xl text-gray-500" />
                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                        <span className="font-semibold">Clique aqui para subir {multiple ? 'arquivos' : 'arquivo'}</span> ou arraste e solte aqui.
                    </p>
                    {config && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Máx. {config.maxFileSizeMb}MB
                            {config.allowedTypes.includes('*') ? '' : ` | ${config.allowedTypes.join(', ')}`}
                            {multiple ? ` | Até ${config.maxFiles} arquivos` : ''}
                        </p>
                    )}
                    <input
                        ref={inputRef}
                        type="file"
                        className="hidden"
                        accept={accept}
                        multiple={multiple}
                        onChange={handleFileInput}
                        disabled={disabled}
                    />
                </div>
            )}

            {/* Error message */}
            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

            {/* File list */}
            {files.length > 0 && (
                <div className="space-y-2">
                    {multiple && (
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                    {label} ({files.length})
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {files.filter(f => f.status === 'success').length} concluído(s)
                                    {files.filter(f => f.status === 'uploading').length > 0 && ` • ${files.filter(f => f.status === 'uploading').length} enviando`}
                                    {files.filter(f => f.status === 'error').length > 0 && ` • ${files.filter(f => f.status === 'error').length} com erro`}
                                </span>
                            </div>
                            <div className="flex gap-3">
                                {selectedIds.size > 0 && (
                                    <button
                                        type="button"
                                        className="text-red-600 cursor-pointer text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={handleRemoveSelected}
                                        disabled={disabled}
                                    >
                                        ({selectedIds.size}) Remover selecionados
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="text-red-500 cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={handleRemoveAll}
                                    disabled={disabled}
                                >
                                    Remover todos
                                </button>
                            </div>
                        </div>
                    )}
                    <div
                        className="overflow-x-auto border border-gray-300 dark:border-gray-600 rounded-lg"
                        style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}
                    >
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300">
                                <tr>
                                    {multiple && (
                                        <th className="py-2 px-3 w-10">
                                            <input
                                                type="checkbox"
                                                checked={allSelected}
                                                onChange={toggleSelectAll}
                                                disabled={disabled}
                                                className="rounded border-gray-300 dark:border-gray-600"
                                            />
                                        </th>
                                    )}
                                    <th className="py-2 px-3">#</th>
                                    <th className="py-2 px-3">Nome do arquivo</th>
                                    <th className="py-2 px-3">Status</th>
                                    <th className="py-2 px-3 text-right">Tamanho</th>
                                    <th className="py-2 px-3 text-center w-24">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {files.map((managed, index) => (
                                    <tr
                                        key={managed.id}
                                        className={`${selectedIds.has(managed.id) ? 'bg-blue-50 dark:bg-blue-900/20' : index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-700/50'}
                                            border-b border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-slate-600`}
                                    >
                                        {multiple && (
                                            <td className="py-2 px-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(managed.id)}
                                                    onChange={() => toggleSelect(managed.id)}
                                                    disabled={disabled}
                                                    className="rounded border-gray-300 dark:border-gray-600"
                                                />
                                            </td>
                                        )}
                                        <td className="py-2 px-3 text-gray-500 dark:text-gray-400">{index + 1}</td>
                                        <td className="py-2 px-3 text-gray-800 dark:text-gray-200">{managed.file.name}</td>
                                        <td className="py-2 px-3">{renderStatus(managed)}</td>
                                        <td className="py-2 px-3 text-right text-gray-500 dark:text-gray-400">
                                            {formatSize(managed.file.size)}
                                        </td>
                                        <td className="py-2 px-3 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                {managed.status === 'error' && managed.retriable !== false && (
                                                    <button
                                                        type="button"
                                                        onClick={() => retry(managed.id)}
                                                        disabled={disabled}
                                                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded text-sm disabled:opacity-50"
                                                        title="Tentar novamente"
                                                    >
                                                        <IoReload />
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveFile(managed.id, managed.file.name)}
                                                    disabled={disabled}
                                                    className="bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white font-bold py-1 px-2 rounded text-sm disabled:opacity-50"
                                                    title="Remover arquivo"
                                                >
                                                    <IoTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FileManager;
